import React, { useEffect, useState } from 'react';
import { App, Form, Input, Modal, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { createProduct, fetchProductCategories } from '../../modules/products/api';
import {
  fetchProductCategoryTags,
  ProductCategory,
} from '../../modules/products/productCategoriesApi';
import { Product } from '../../types/pz';

const BASE_UNITS = [
  'PIECE',
  'KILOGRAM',
  'METER',
  'LINEAR_METER',
  'SQUARE_METER',
  'CUBIC_METER',
  'LITER',
  'SET',
  'PACKAGE',
] as const;

interface AddProductModalProps {
  open: boolean;
  onCancel: () => void;
  /** Called with the newly-created product so the caller can select its variant. */
  onCreated: (product: Product) => void;
}

const AddProductModal: React.FC<AddProductModalProps> = ({ open, onCancel, onCreated }) => {
  const { t } = useTranslation(['products', 'common']);
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryTags, setCategoryTags] = useState<ProductCategory[]>([]);

  // Fetch classification enum values once on first open.
  useEffect(() => {
    if (!open) return;
    fetchProductCategories()
      .then((cats) => setCategories(cats))
      .catch(() => {
        // Non-fatal: fall back to hardcoded list so the modal stays usable.
        setCategories(['RAW_MATERIAL', 'FINISHED_GOOD', 'PACKAGING', 'COMPONENT', 'SEMI_FINISHED', 'OTHER']);
      });
    fetchProductCategoryTags()
      .then(setCategoryTags)
      .catch(() => setCategoryTags([]));
  }, [open]);

  const handleOk = async () => {
    let values: { name: string; category: string; productCategoryId: string; baseUnit: string };
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setSubmitting(true);
    try {
      const product = await createProduct({
        name: values.name,
        category: values.category,
        productCategoryId: values.productCategoryId,
        baseUnit: values.baseUnit,
      });
      message.success(t('products:addProductModal.createSuccess'));
      form.resetFields();
      onCreated(product);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('products:addProductModal.createFailed');
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={t('products:addProductModal.title')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={submitting}
      okText={t('common:save')}
      cancelText={t('common:cancel')}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ category: 'FINISHED_GOOD', baseUnit: 'PIECE' }}
      >
        <Form.Item
          name="name"
          label={t('products:addProductModal.nameLabel')}
          rules={[{ required: true, message: t('products:addProductModal.nameRequired') }]}
        >
          <Input placeholder={t('products:placeholders.name')} maxLength={255} />
        </Form.Item>

        <Form.Item
          name="productCategoryId"
          label={t('products:fields.category')}
          rules={[{ required: true, message: t('products:validation.categoryRequired') }]}
        >
          <Select
            showSearch
            optionFilterProp="label"
            options={categoryTags.map((category) => ({ value: category.id, label: category.name }))}
            placeholder={t('products:placeholders.category')}
          />
        </Form.Item>

        <Form.Item
          name="category"
          label={t('products:addProductModal.classificationLabel')}
          rules={[{ required: true, message: t('products:addProductModal.classificationRequired') }]}
        >
          <Select>
            {categories.map((cat) => (
              <Select.Option key={cat} value={cat}>
                {t(`products:categories.${cat}`, { defaultValue: cat })}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="baseUnit"
          label={t('products:addProductModal.baseUnitLabel')}
          rules={[{ required: true, message: t('products:addProductModal.baseUnitRequired') }]}
        >
          <Select>
            {BASE_UNITS.map((unit) => (
              <Select.Option key={unit} value={unit}>
                {t(`products:units.${unit}`, { defaultValue: unit })}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddProductModal;
